import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal from 'components/shared/StandardModal';
import { simpleDateAtTime } from 'lib/date';
import PortalConnection from 'models/PortalConnection';
import { PortalConnectionBlocker } from 'services/api/portalConnections';
import { BodySmall } from 'styles/typography';

// Why a portal can't be scanned, in words. Keyed off the row's `scanBlockers` — the server's own
// verdict on the scan axis, which is narrower than `blockers` (a missing RETRIEVAL script doesn't
// stop a sweep, and manual mode does).
const SCAN_BLOCKER_REASONS: Record<PortalConnectionBlocker, string> = {
  no_credentials: 'no saved password',
  credentials_invalid: 'the saved password was rejected',
  no_retrieval_script: 'no recording yet',
  no_discovery_script: 'no scan recorded yet',
  manual_mode: 'this portal is handled by hand',
  no_coordinating_group: 'no coordinating group to receive the results',
};

export function scanBlockerReason(connection: PortalConnection): string {
  const reasons = connection.scanBlockers.map((blocker) => SCAN_BLOCKER_REASONS[blocker]).filter(Boolean);
  // Never blank: a portal offered as unscannable without a reason is worse than one that admits
  // the reason didn't reach the UI.
  return reasons.length > 0 ? reasons.join(', ') : 'not ready to scan';
}

interface Props {
  connections: PortalConnection[];
  submitting: boolean;
  onCancel: () => void;
  onStart: (sourceSystems: string[]) => void;
}

/**
 * `Start a Portal Scan` — a MULTI-select, so one gesture can start several scans (one
 * `portal_discovery` run per checked portal, never one run covering several).
 *
 * A portal that already has a live scan is still offered: the api's singleton key resolves the
 * request to the run in flight and reports `created: false`, which the caller reports honestly.
 * Suppressing the row here would leave the user with no explanation for why it wasn't started.
 */
export default function StartScanModal({ connections, submitting, onCancel, onStart }: Props) {
  const [checked, setChecked] = useState<string[]>([]);

  const toggle = (sourceSystem: string) =>
    setChecked((current) =>
      current.includes(sourceSystem) ? current.filter((entry) => entry !== sourceSystem) : [...current, sourceSystem]
    );

  return (
    <StandardModal title='Start a Portal Scan' onCancel={onCancel} closeOnBackdropClick>
      <Prompt>Select which portals to start a scan for:</Prompt>
      <Choices data-testid='start-scan-choices'>
        {connections.map((connection) => (
          <Choice key={connection.sourceSystem} $disabled={!connection.canScan}>
            <input
              type='checkbox'
              // The label wraps a name AND a status line, so the accessible name is spelled out
              // here rather than inherited from all of it.
              aria-label={connection.displayName}
              checked={checked.includes(connection.sourceSystem)}
              disabled={!connection.canScan}
              onChange={() => toggle(connection.sourceSystem)}
            />
            <ChoiceText>
              <span>{connection.displayName}</span>
              <Sub>
                {connection.canScan
                  ? // The connection row's own lastScanAt, which the api derives from the newest
                    // COMPLETED sweep's completion — the same value the scan card renders, so this
                    // line and the card below it cannot disagree. Null until a sweep has finished,
                    // which is why a portal mid-first-scan still reads "Never scanned".
                    connection.lastScanAt
                    ? `Last scanned on ${simpleDateAtTime(connection.lastScanAt)}`
                    : 'Never scanned'
                  : `Can’t scan — ${scanBlockerReason(connection)}`}
              </Sub>
            </ChoiceText>
          </Choice>
        ))}
      </Choices>
      <Actions>
        <Button variant='ghost' onClick={onCancel} data-testid='start-scan-cancel'>
          Cancel
        </Button>
        <Button
          onClick={() => onStart(checked)}
          disabled={checked.length === 0 || submitting}
          loading={submitting}
          data-testid='start-scan-submit'>
          Start Scan
        </Button>
      </Actions>
    </StandardModal>
  );
}

const Prompt = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black75};
`;

const Choices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0 28px;
`;

const Choice = styled.label<{ $disabled: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`;

const ChoiceText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Sub = styled(BodySmall)`
  color: ${({ theme }) => theme.colors.black50};
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;
